/**
 * Collapses an "all-empty" filter object to `undefined` so it produces
 * the same query-cache key as a no-arg call.
 *
 * Why this matters for offline UX:
 *
 * The pre-warm hook (`usePrewarmCache`) calls every list endpoint with
 * NO filter — caching it under e.g. `['articles', undefined]`. List
 * pages, however, call `useArticles({ search, categoryId, isActive })`
 * — and even when the user hasn't typed anything, those properties are
 * `undefined`, producing the key `['articles', { search: undefined,
 * categoryId: undefined, isActive: undefined }]`. TanStack Query hashes
 * these as DIFFERENT entries, so the page misses the warm cache and
 * shows blank when offline. This normalises the page key back to the
 * pre-warm key whenever the user hasn't actively narrowed the list.
 *
 * Active filters (anything that is not nullish or empty-string) pass
 * through untouched so they get their own cache entry.
 */
export function normalizeQueryFilter<T extends object>(filter: T | undefined): T | undefined {
  if (!filter) return undefined;
  for (const v of Object.values(filter)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    return filter;
  }
  return undefined;
}
